import { createClient } from "@procore/core-http";

import { getProcoreApiBaseUrl } from "./auth";

/**
 * Shared Procore HTTP Client
 *
 * Automatically includes:
 * - Authorization header (OAuth token from env if available)
 * - Procore-Company-Id header (if companyId set)
 * - baseUrl (via getProcoreApiBaseUrl: /procore-api for local dev, empty for production)
 * - credentials setting (omit for token auth, include for cookie auth)
 *
 * Authentication:
 * - Uses REACT_APP_PROCORE_ACCESS_TOKEN if set (local development with OAuth)
 * - Falls back to cookie-based auth (production/embedded in Procore)
 *
 * Design Note:
 * - The defaults() function is evaluated per-request (by design of @procore/core-http)
 * - companyId is set once at Webviewer initialization and never changes within a session
 * - CompanyId is optional in headers; endpoints requiring it will fail gracefully if missing
 *
 * API modules specify their own version in the path (e.g., `/rest/v1.0/analytic_events`)
 */

let _companyId: number | undefined;

/**
 * Set the Procore company ID used in all outgoing API request headers.
 * Called once during Webviewer initialization when the Procore context is known.
 */
export function setCompanyId(id: number): void {
    _companyId = id;
}

export const procoreClient = createClient({
    defaults: () => {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };

        if (_companyId) {
            headers["Procore-Company-Id"] = String(_companyId);
        }

        // Add OAuth token if available (for local development)
        const accessToken = import.meta.env.REACT_APP_PROCORE_ACCESS_TOKEN;
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        // Use centralized baseUrl logic:
        // - Local dev with token: /procore-api (Vite proxy)
        // - Production: empty string (relative paths on same domain)
        // - Override: REACT_APP_PROCORE_API_BASE_URL if explicitly set
        const baseUrl = getProcoreApiBaseUrl();

        // Set credentials based on auth type:
        // - Token auth: omit cookies
        // - Cookie auth: include cookies for Procore session
        const credentials: RequestCredentials = accessToken ? "omit" : "include";

        return {
            headers,
            baseUrl,
            credentials,
        };
    },
});
